import { config, TWIN_TYPE, nodeSymbolHalfHeight } from './config';

export const spousePath = (
  uNode,
  cNode,
  parentRelationship = 'married',
  isConsanguineous = false,
  laneY = null
) => {
  const { x: ux, y: uy } = uNode;
  const { x: cx, y: cy } = cNode;

  // Gap between the two parallel lines of a consanguineous union. Tied to the
  // symbol size (which scales with zoom) rather than a fixed 15px: the
  // relationship stroke is non-scaling (~constant on screen), so a fixed
  // coordinate-space gap shrinks below the line width when zoomed out and the
  // two lines merge into one. A symbol-proportional gap stays distinguishable
  // at any zoom.
  const line_gap = config.SYMBOL_SIZE * 0.2;

  // STRETCHED route (a decoupled SECONDARY containment bridge whose partners sit in
  // different family columns — G-1-01373's 25-35): drop each partner to a horizontal lane
  // BELOW the row, run the long marriage bar there, and come back up to the other partner.
  // This keeps the bar off the row of nodes it would otherwise run straight over. The
  // shared child drops from the bar (its union node sits ON the lane). divorced/separated
  // ticks are omitted for the stretched form (it is a distant-cousin bar, not a divorce).
  if (laneY != null && Number.isFinite(laneY) && Math.abs(ux - cx) >= 1) {
    let path = `M${ux},${uy} L${ux},${laneY} L${cx},${laneY} L${cx},${cy}`;
    if (isConsanguineous) {
      const g = line_gap;
      path += ` M${ux},${uy} L${ux},${laneY + g} L${cx},${
        laneY + g
      } L${cx},${cy}`;
    }
    return path;
  }

  let path = `M${ux},${uy} L${cx},${cy}`;
  if (isConsanguineous) {
    path += ` M${ux},${uy - line_gap} L${cx},${cy - line_gap}`;
  }

  const mx = (ux + cx) / 2;
  const my = (uy + cy) / 2;
  const line_height = 32;

  if (parentRelationship === 'divorced') {
    path +=
      ` M${mx + 20},${my - line_height} L${mx - 20},${my + line_height}` +
      ` M${mx + 40},${my - line_height} L${mx},${my + line_height}`;
  } else if (parentRelationship === 'separated') {
    path += ` M${mx + 20},${my - line_height} L${mx - 20},${my + line_height}`;
  }

  return path;
};

export const childPath = (uNode, cNode, laneY = null) => {
  let d;
  const { x: ux, y: uy } = uNode;
  const { x: cx, y: cy } = cNode;

  // offset is set to zero for dev purpose
  const offSet = 0;

  // OVER-THE-TOP route (a displaced sibship whose connector would otherwise sweep an
  // unrelated co-generation): the child sits IN LINE with its generation and the
  // sibling-line runs HIGHER, parallel to and above the generation row, at laneY (above
  // the union). Up from the union, across at laneY, then down to the child.
  if (laneY != null && Math.abs(ux - cx) >= 1) {
    return `M${ux},${uy} L${ux},${laneY} L${cx},${laneY} L${cx},${cy - offSet}`;
  }

  // Sub-pixel float error from layout arithmetic (e.g. 6600 vs 6599.999999999999)
  // must not turn a genuinely-vertical drop into a dog-leg; a 1px tolerance is far
  // below the symbol-width gap of any real dog-leg.
  if (Math.abs(ux - cx) < 1) {
    // Case 1: same x → straight vertical
    d = `M${ux},${uy} L${ux},${cy - offSet}`;
  } else {
    // Case 2: different x → elbow
    const midY = (uy + cy) / 2;
    d = `M${ux},${uy} 
         L${ux},${midY} 
         L${cx},${midY} 
         L${cx},${cy - offSet}`;
  }

  return d;
};

// Gamete-donor line of descent (Bennett Fig 5): a single STRAIGHT DIAGONAL from the
// bottom of the donor node — NOT the orthogonal elbow of a normal child edge. The donor
// sits on the parents' row and the offspring one row below, so the line reads as a
// diagonal converging on the pregnancy, where the carrier's own vertical descent also
// arrives (Bennett shows several such lines meeting there). `fromNode` is the donor
// (full-size); `toNode` is the offspring (often the smaller pregnancy symbol).
//
// Target depends on whether the donor produced a WHOLE sibship (Bennett 5.1c). With no
// `carrierNode`, aim at the offspring's TOP edge (single offspring, or offspring in
// different sibships → one diagonal each). With a `carrierNode`, the offspring shares a
// sibship with siblings from the same donor: aim instead at the right-angle CORNER of
// the sibship bar — the elbow midpoint (carrier.y + child.y)/2, matching childPath's bar
// — so ONE line feeds the whole sibship rather than one line per child.
// `aimPoint` overrides the target when the offspring's drop does not come off an ordinary sibship
// bar. That is the TWIN case: a twin pregnancy's drop forks at an apex partway down, so a line to
// the sibship-bar corner visibly misses the fork (reported in QA). The caller passes the apex.
export const reproDescentPath = (
  fromNode,
  toNode,
  carrierNode = null,
  aimPoint = null
) => {
  const x1 = fromNode.x;
  const y1 = fromNode.y + nodeSymbolHalfHeight(fromNode); // donor bottom edge
  if (aimPoint && Number.isFinite(aimPoint.x) && Number.isFinite(aimPoint.y)) {
    return `M${x1},${y1} L${aimPoint.x},${aimPoint.y}`;
  }
  const x2 = toNode.x;
  const y2 =
    carrierNode != null
      ? (carrierNode.y + toNode.y) / 2 // sibship-bar right-angle corner (Bennett 5.1c)
      : toNode.y - nodeSymbolHalfHeight(toNode); // offspring top edge
  return `M${x1},${y1} L${x2},${y2}`;
};

// Dual-parent adoption (Bennett Fig 3): a SOLID reference connector from an adopted-in child UP
// to its in-graph biological parents. Aims at the midpoint of whichever bio parents are present
// (their marriage-bar level when both are given, the single parent's centre otherwise); the
// child end attaches at its top edge so the line reads as a descent distinct from the dashed
// adoptive line. Shared by the engine (edge emission) and the live drag so the geometry can't
// drift. Straight line — crossing-aware routing is a deferred refinement.
export const bioConnectorPath = (childNode, parentNodes) => {
  const parents = (parentNodes || []).filter(
    (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y)
  );
  if (!childNode || !Number.isFinite(childNode.x) || parents.length === 0) {
    return '';
  }
  const x2 = parents.reduce((s, p) => s + p.x, 0) / parents.length;
  const y2 = parents.reduce((s, p) => s + p.y, 0) / parents.length;
  const x1 = childNode.x;
  const y1 = childNode.y - nodeSymbolHalfHeight(childNode); // child top edge
  return `M${x1},${y1} L${x2},${y2}`;
};

// The APEX of a twin group: the point the parents' drop reaches and from which the slanted lines
// fan out to each twin. Exported because more than one thing must aim at it — the twin path itself
// and a gamete-donor's line of descent, which for a twin pregnancy has to meet the fork rather than
// the ordinary sibship bar. One formula, so the two can never disagree (the same reason unionVX and
// buildRenderModel share their anchor rule).
export const twinApex = (unionNode, twinNodes) => {
  if (!unionNode || !twinNodes || twinNodes.length === 0) return null;
  const xs = twinNodes.map((t) => t.x).filter((x) => Number.isFinite(x));
  if (!xs.length || !Number.isFinite(unionNode.y)) return null;
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const twinY = twinNodes[0].y;
  return {
    x: centerX,
    y: unionNode.y + (twinY - unionNode.y) * config.TWIN_DIAGONAL_MEETING_HEIGHT
  };
};

export const twinGroupPath = (unionNode, twinNodes) => {
  if (!twinNodes || twinNodes.length === 0) {
    return { path: '', unknownMarkers: [] };
  }

  const { x: ux, y: uy } = unionNode;

  // Horizontal centre + meeting point, from the shared helper so the donor line can aim at the
  // identical apex.
  const apex = twinApex(unionNode, twinNodes);
  const centerX = apex.x;
  const twinY = twinNodes[0].y;
  const meetingY = apex.y;

  // bar position
  const barHeightRatio = 0.5;
  const barY = meetingY + (twinY - meetingY) * barHeightRatio;

  let path = '';

  // Elbow line from union node down to meeting point
  // Vertical down from union, then horizontal to center
  path += `M ${ux},${uy} L ${ux},${meetingY} L ${centerX},${meetingY}`;

  // Diagonal lines from meeting point to each twin
  twinNodes.forEach((twin) => {
    path += ` M ${centerX},${meetingY} L ${twin.x},${twin.y}`;
  });

  const processedPairs = new Set();
  const unknownMarkers = [];

  // Helper to calculate x position on diagonal line at given y
  const getXOnDiagonal = (twinX, twinY) => {
    if (twinY === meetingY) return twinX; // Avoid division by zero
    return (
      centerX + ((barY - meetingY) * (twinX - centerX)) / (twinY - meetingY)
    );
  };

  twinNodes.forEach((twin) => {
    if (!twin.twinRelations || !Array.isArray(twin.twinRelations)) return;

    twin.twinRelations.forEach((rel) => {
      const pairKey = [twin.pid, rel.withPid].sort().join('-');
      if (processedPairs.has(pairKey)) return;

      const otherTwin = twinNodes.find((t) => t.pid === rel.withPid);
      if (!otherTwin) return;

      const isConfirmedPair = otherTwin.twinRelations?.some(
        (r) => r.withPid === twin.pid && r.type === rel.type
      );
      if (!isConfirmedPair) return;

      processedPairs.add(pairKey);

      const twinBarX = getXOnDiagonal(twin.x, twin.y);
      const otherBarX = getXOnDiagonal(otherTwin.x, otherTwin.y);

      if (rel.type === TWIN_TYPE.monozygotic) {
        // Draw horizontal bar between the two diagonal lines
        const barStartX = Math.min(twinBarX, otherBarX);
        const barEndX = Math.max(twinBarX, otherBarX);
        path += ` M ${barStartX},${barY} L ${barEndX},${barY}`;
      } else if (rel.type === TWIN_TYPE.unknown) {
        // Record midpoint for "?" text marker
        unknownMarkers.push({ x: (twinBarX + otherBarX) / 2, y: barY });
      }
    });
  });

  return { path, unknownMarkers };
};
